import React, { useState, useEffect, useCallback, useMemo, memo } from "react";
import { useTranslation } from "react-i18next";
import { Modal } from "../ui/Modal";
import { Button } from "../ui/Button";
import { FileUploader } from "../ui/FileUploader";
import { Spinner } from "../ui/Spinner";
import { Alert } from "../ui/Alert";
import { Input } from "../ui/Input";
import { Text } from "../ui/Text";
import { Heading } from "../ui/Heading";
import { Stack } from "../ui/Stack";
import { useApi } from "../../context/ApiContext";
import { useStore } from "../../context/StoreContext";
import SearchableDropdown from "../SearchableDropdown";
import { TranslatableField } from "../common/TranslatableField";
import { SupportedLanguage } from "../../types/translations.types";
import { ModalLanguageProvider, useModalLanguage } from "../../context/ModalLanguageContext";
import { LanguageSwitcher } from "../common/LanguageSwitcher";
import { GalleryImageSelector } from "./GalleryImageSelector";

interface Category {
 _id?: string;
 name: string;
 slug?: string;
 description?: string;
 image?: string;
 parent?: string;
 isActive: boolean;
 translations?: Record<string, Record<SupportedLanguage, string>>;
}

interface CategoryFormModalProps {
 isOpen: boolean;
 onClose: () => void;
 categoryId?: string;
 onSuccess: () => void;
 categories: Category[]; // For parent selection
}

export const CategoryFormModal: React.FC<CategoryFormModalProps> = ({
 isOpen,
 onClose,
 categoryId,
 onSuccess,
 categories,
}) => {
 const { t } = useTranslation();
 const { api } = useApi();
 const { isPluginEnabled } = useStore();
 const isProfitPluginEnabled = isPluginEnabled("profit-rules");
 const [loading, setLoading] = useState(!!categoryId);
 const [saving, setSaving] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [formData, setFormData] = useState<Partial<Category>>({
  name: "",
  description: "",
  parent: "",
  isActive: true,
  translations: {},
 });
 
 // Profit Rules state
 const [roles, setRoles] = useState<any[]>([]);
 const [categoryRolePercentages, setCategoryRolePercentages] = useState<Record<string, number>>({});
 const [loadingProfitRules, setLoadingProfitRules] = useState(false);

 const loadProfitRules = useCallback(async () => {
  if (!categoryId || !isProfitPluginEnabled) return;
  
  try {
   setLoadingProfitRules(true);
   const [rolesResponse, rulesResponse] = await Promise.all([
    api.get("/profit-rules/roles"),
    api.get(`/profit-rules/categories/${categoryId}/rules`).catch(() => ({ data: null })),
   ]);
   
   setRoles(rolesResponse.data || []);
   
   if (rulesResponse.data && rulesResponse.data.rolePercentages) {
    const percentages: Record<string, number> = {};
    if (rulesResponse.data.rolePercentages instanceof Map) {
     rulesResponse.data.rolePercentages.forEach((value: number, key: string) => {
      percentages[key] = value;
     });
    } else if (typeof rulesResponse.data.rolePercentages === "object") {
     Object.assign(percentages, rulesResponse.data.rolePercentages);
    }
    setCategoryRolePercentages(percentages);
   } else {
    setCategoryRolePercentages({});
   }
  } catch (err: any) {
   // 404 is fine - no rules exist yet
   if (err.response?.status !== 404) {
    console.error("Failed to load profit rules:", err);
   }
   setCategoryRolePercentages({});
  } finally {
   setLoadingProfitRules(false);
  }
 }, [api, categoryId, isProfitPluginEnabled]);

 const fetchCategory = useCallback(async () => {
  try {
   setLoading(true);
   setError(null);
   const response = await api.get(`/categories/${categoryId}`);
   setFormData(response.data);
   
   // Load profit rules if category exists and plugin is enabled
   if (categoryId && isProfitPluginEnabled) {
    await loadProfitRules();
   }
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.errors.fetchCategory"));
  } finally {
   setLoading(false);
  }
 }, [api, categoryId, t, loadProfitRules]);

 useEffect(() => {
  if (isOpen) {
   if (categoryId) {
    fetchCategory();
   } else {
    setFormData({
     name: "",
     description: "",
     parent: "",
     isActive: true,
     translations: {},
    });
    setError(null);
    setCategoryRolePercentages({});
   }
  }
 }, [isOpen, categoryId, fetchCategory]);

 const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  try {
   setSaving(true);
   setError(null);

   // Prepare translations payload
   const translations: any = {};
   if (formData.translations) {
    Object.keys(formData.translations).forEach((field) => {
     if (formData.translations && formData.translations[field]) {
      translations[field] = formData.translations[field];
     }
    });
   }

   const payload = {
    ...formData,
    parent: formData.parent || undefined,
    translations: Object.keys(translations).length > 0 ? translations : undefined,
   };
   
   // Remove slug from payload - server will auto-generate it
   delete payload.slug;

   let savedCategoryId = categoryId;
   if (categoryId) {
    await api.patch(`/categories/${categoryId}`, payload);
   } else {
    const response = await api.post("/categories", payload);
    savedCategoryId = response.data._id;
   }

   // Save profit rules if category exists (either was existing or just created) and plugin is enabled
   if (savedCategoryId && isProfitPluginEnabled) {
    // Check if all percentages are empty or zero
    const hasAnyPercentage = Object.values(categoryRolePercentages).some((p) => p && p > 0);
    
    // If no percentages are set, delete the rules (if they exist)
    if (!hasAnyPercentage) {
     try {
      await api.delete(`/profit-rules/categories/${savedCategoryId}/rules`);
     } catch (err: any) {
      // If rules don't exist (404), that's fine
      if (err.response?.status !== 404) {
       throw err;
      }
     }
    } else {
     // Validate total percentage
     const totalPercentage = Object.values(categoryRolePercentages).reduce(
      (sum, p) => sum + (p || 0),
      0
     );

     if (totalPercentage > 100) {
      setError(t("admin.profitRulesSettings.categoryRules.errors.percentageExceeds100"));
      return;
     }

     // Save profit rules
     await api.post(`/profit-rules/categories/${savedCategoryId}/rules`, {
      rolePercentages: categoryRolePercentages,
     });
    }
   }

   onSuccess();
   onClose();
  } catch (err: any) {
   setError(err.response?.data?.error || t("admin.errors.saveCategory"));
  } finally {
   setSaving(false);
  }
 };

 const handleImageUpload = useCallback((url: string) => {
  setFormData((prev) => ({ ...prev, image: url }));
 }, []);

 const handleImageRemove = useCallback(() => {
  setFormData((prev) => ({ ...prev, image: undefined }));
 }, []);

 const handleNameChange = useCallback((language: SupportedLanguage, value: string) => {
  if (language === "en") {
   setFormData((prev) => ({ ...prev, name: value }));
  } else {
   setFormData((prev) => {
    const translations = prev.translations || {};
    const fieldTranslations = translations.name || {};
    fieldTranslations[language] = value;
    return {
     ...prev,
     translations: {
      ...translations,
      name: fieldTranslations,
     },
    };
   });
  }
 }, []);

 const handleDescriptionChange = useCallback((language: SupportedLanguage, value: string) => {
  if (language === "en") {
   setFormData((prev) => ({ ...prev, description: value }));
  } else {
   setFormData((prev) => {
    const translations = prev.translations || {};
    const fieldTranslations = translations.description || {};
    fieldTranslations[language] = value;
    return {
     ...prev,
     translations: {
      ...translations,
      description: fieldTranslations,
     },
    };
   });
  }
 }, []);

 // Memoize label strings to prevent re-renders
 const nameLabel = useMemo(() => t("admin.categoryName"), [t]);
 const descriptionLabel = useMemo(() => t("admin.description"), [t]);

 // Memoize translations to prevent entity reference changes from causing re-renders
 // Only recalculate when translations actually change
 const nameTranslations = useMemo(() => {
  return formData.translations?.name;
 }, [formData.translations?.name]);
 
 const descriptionTranslations = useMemo(() => {
  return formData.translations?.description;
 }, [formData.translations?.description]);
 
 // Create stable entity references that only change when translations actually change
 // For English mode, the entity doesn't need to include the current value since we use the value prop
 // This prevents the entity from changing on every keystroke
 const nameEntity = useMemo(() => {
  return {
   name: "", // Always empty for English - we use value prop instead
   translations: nameTranslations ? { name: nameTranslations } : undefined,
  };
 }, [nameTranslations]);
 
 const descriptionEntity = useMemo(() => {
  return {
   description: "", // Always empty for English - we use value prop instead
   translations: descriptionTranslations ? { description: descriptionTranslations } : undefined,
  };
 }, [descriptionTranslations]);

 // Memoize other labels and callbacks
 const parentCategoryLabel = useMemo(() => t("admin.categoryForm.parentCategory"), [t]);
 const statusLabel = useMemo(() => t("admin.status"), [t]);
 const categoryImageLabel = useMemo(() => t("admin.categoryForm.categoryImage"), [t]);
 
 const handleParentSelect = useCallback((option: { _id: string }) => {
  setFormData((prev) => ({ ...prev, parent: option._id || "" }));
 }, []);
 
 const handleStatusSelect = useCallback((option: { _id: string }) => {
  setFormData((prev) => ({ ...prev, isActive: option._id === "active" }));
 }, []);

 // Memoize the parent category options fetcher
 const fetchParentOptions = useCallback(async (search: string) => {
  const filtered = categories
   .filter((cat) => cat._id !== categoryId)
   .filter((cat) => cat.name.toLowerCase().includes(search.toLowerCase()))
   .map((cat) => ({
    _id: cat._id,
    label: cat.name,
   }));
  return [{ _id: "", label: t("admin.categoryForm.noneRoot") }, ...filtered];
 }, [categories, categoryId, t]);

 // Memoize the status options fetcher
 const fetchStatusOptions = useCallback(async (search: string) => {
  const statuses = [
   { _id: "active", label: t("admin.active") },
   { _id: "inactive", label: t("admin.inactive") },
  ];
  return statuses.filter((status) =>
   status.label.toLowerCase().includes(search.toLowerCase())
  );
 }, [t]);

 // Memoize getDisplayLabel functions
 // Use useMemo to create stable categories reference based on content, not reference
 const categoriesKey = useMemo(() => JSON.stringify(categories.map(c => ({ _id: c._id, name: c.name }))), [categories]);
 const getParentDisplayLabel = useCallback((val: string) => {
  if (!val) return t("admin.categoryForm.noneRoot");
  const cat = categories.find((c) => c._id === val);
  return cat?.name || "";
 }, [categoriesKey, t, categories]);

 const getStatusDisplayLabel = useCallback((val: string) => {
  return val === "active" ? t("admin.active") : t("admin.inactive");
 }, [t]);

 const handlePercentageChange = useCallback((roleId: string, value: string) => {
  const numValue = value === "" ? 0 : parseFloat(value);
  if (isNaN(numValue) || numValue < 0) return;
  
  setCategoryRolePercentages((prev) => ({
   ...prev,
   [roleId]: numValue,
  }));
 }, []);


 // Memoize existing images array
 const existingImages = useMemo(() => {
  return formData.image ? [{ url: formData.image, isPrimary: true }] : [];
 }, [formData.image]);

 // Memoize modal title and footer to prevent re-renders
 const modalTitle = useMemo(() => categoryId ? t("admin.editCategory") : t("admin.addCategory"), [categoryId, t]);
 const cancelLabel = useMemo(() => t("common.cancel"), [t]);
 const submitLabel = useMemo(() => categoryId ? t("admin.updateCategory") : t("admin.addCategory"), [categoryId, t]);

 // Inline ModalWithLanguage directly to prevent component recreation on every render
 // The key issue: defining components inside causes React to treat them as new component types
 return (
  <ModalLanguageProvider>
   <ModalContent
    loading={loading}
    isOpen={isOpen}
    onClose={onClose}
    saving={saving}
    error={error}
    modalTitle={modalTitle}
    cancelLabel={cancelLabel}
    submitLabel={submitLabel}
    formData={formData}
    nameLabel={nameLabel}
    descriptionLabel={descriptionLabel}
    nameEntity={nameEntity}
    descriptionEntity={descriptionEntity}
    handleNameChange={handleNameChange}
    handleDescriptionChange={handleDescriptionChange}
    handleSubmit={handleSubmit}
    parentCategoryLabel={parentCategoryLabel}
    statusLabel={statusLabel}
    categoryImageLabel={categoryImageLabel}
    fetchParentOptions={fetchParentOptions}
    fetchStatusOptions={fetchStatusOptions}
    getParentDisplayLabel={getParentDisplayLabel}
    getStatusDisplayLabel={getStatusDisplayLabel}
    handleParentSelect={handleParentSelect}
    handleStatusSelect={handleStatusSelect}
    handleImageUpload={handleImageUpload}
    handleImageRemove={handleImageRemove}
    existingImages={existingImages}
    categoryId={categoryId}
    roles={roles}
    categoryRolePercentages={categoryRolePercentages}
    loadingProfitRules={loadingProfitRules}
    handlePercentageChange={handlePercentageChange}
    isProfitPluginEnabled={isProfitPluginEnabled}
    t={t}
   />
  </ModalLanguageProvider>
 );
};

// Define ModalContent OUTSIDE the main component to prevent recreation
interface ModalContentProps {
 loading: boolean;
 isOpen: boolean;
 onClose: () => void;
 saving: boolean;
 error: string | null;
 modalTitle: string;
 cancelLabel: string;
 submitLabel: string;
 formData: Partial<Category>;
 nameLabel: string;
 descriptionLabel: string;
 nameEntity: any;
 descriptionEntity: any;
 handleNameChange: (language: SupportedLanguage, value: string) => void;
 handleDescriptionChange: (language: SupportedLanguage, value: string) => void;
 handleSubmit: (e: React.FormEvent) => void;
 parentCategoryLabel: string;
 statusLabel: string;
 categoryImageLabel: string;
 fetchParentOptions: (search: string) => Promise<any[]>;
 fetchStatusOptions: (search: string) => Promise<any[]>;
 getParentDisplayLabel: (val: string) => string;
 getStatusDisplayLabel: (val: string) => string;
 handleParentSelect: (option: { _id: string }) => void;
 handleStatusSelect: (option: { _id: string }) => void;
 handleImageUpload: (url: string) => void;
 handleImageRemove: () => void;
 existingImages: any[];
 categoryId?: string;
 roles: any[];
 categoryRolePercentages: Record<string, number>;
 loadingProfitRules: boolean;
 handlePercentageChange: (roleId: string, value: string) => void;
 isProfitPluginEnabled: boolean;
 t: any;
}

const ModalContent: React.FC<ModalContentProps> = memo(({
 loading,
 isOpen,
 onClose,
 saving,
 error,
 modalTitle,
 cancelLabel,
 submitLabel,
 formData,
 nameLabel,
 descriptionLabel,
 nameEntity,
 descriptionEntity,
 handleNameChange,
 handleDescriptionChange,
 handleSubmit,
 parentCategoryLabel,
 statusLabel,
 categoryImageLabel,
 fetchParentOptions,
 fetchStatusOptions,
 getParentDisplayLabel,
 getStatusDisplayLabel,
 handleParentSelect,
 handleStatusSelect,
 handleImageUpload,
 handleImageRemove,
 existingImages,
 categoryId,
 roles,
 categoryRolePercentages,
 loadingProfitRules,
 handlePercentageChange,
 isProfitPluginEnabled,
 t,
}) => {
 const { currentLanguage, setCurrentLanguage } = useModalLanguage();
 
 if (loading) {
  return (
   <Modal isOpen={isOpen} onClose={onClose} title={modalTitle}>
    <div className="flex justify-center items-center py-8">
     <Spinner size="lg" />
    </div>
   </Modal>
  );
 }

 return (
  <Modal
   isOpen={isOpen}
   onClose={onClose}
   title={modalTitle}
   headerActions={
    <LanguageSwitcher
     currentLanguage={currentLanguage}
     onLanguageChange={setCurrentLanguage}
     showLabel={false}
    />
   }
   footer={
    <div className="flex gap-2 justify-end">
     <Button type="button" variant="outline" onClick={onClose} disabled={saving}>
      {cancelLabel}
     </Button>
     <Button type="submit" form="category-form" disabled={saving}>
      {saving ? <Spinner size="sm" /> : submitLabel}
     </Button>
    </div>
   }
  >
   <form id="category-form" onSubmit={handleSubmit} className="space-y-4">
    {error && <Alert variant="error">{error}</Alert>}

    <TranslatableField
     key="category-name-field"
     label={nameLabel}
     fieldName="name"
     entity={nameEntity}
     value={formData.name || ""}
     onChange={handleNameChange}
     required
    />

    <TranslatableField
     key="category-description-field"
     label={descriptionLabel}
     fieldName="description"
     entity={descriptionEntity}
     value={formData.description || ""}
     onChange={handleDescriptionChange}
     type="textarea"
     rows={3}
    />

    <div>
     <label className="block text-sm font-medium text-gray-700 mb-2">
      {parentCategoryLabel}
     </label>
     <SearchableDropdown
      fetchOptions={fetchParentOptions}
      placeholder={t("admin.productForm.searchCategory")}
      value={formData.parent || ""}
      getDisplayLabel={getParentDisplayLabel}
      onSelect={handleParentSelect}
     />
    </div>

    <div>
     <label className="block text-sm font-medium text-gray-700 mb-2">
      {statusLabel}
     </label>
     <SearchableDropdown
      fetchOptions={fetchStatusOptions}
      placeholder={t("admin.productForm.selectStatus")}
      value={formData.isActive ? "active" : "inactive"}
      getDisplayLabel={getStatusDisplayLabel}
      onSelect={handleStatusSelect}
     />
    </div>

    {/* Image Upload */}
    <div className="space-y-4">
     <GalleryImageSelector
      onSelect={handleImageUpload}
      label={t("admin.gallery.selectFromGallery")}
      buttonText={t("admin.gallery.selectFromGallery")}
     />
     <FileUploader
      label={categoryImageLabel}
      onUploadComplete={handleImageUpload}
      existingImages={existingImages}
      onImageRemove={handleImageRemove}
     />
    </div>

    {/* Profit Rules Section - Only show when editing existing category and plugin is enabled */}
    {categoryId && isProfitPluginEnabled && (
     <div className="border-t pt-4 mt-4">
      <Stack spacing="md">
       <Heading size="sm">{t("admin.profitRulesSettings.categoryRules.title")}</Heading>
       <Text className="text-sm text-gray-600 dark:text-gray-400">
        {t("admin.profitRulesSettings.categoryRules.description")}
       </Text>

       {loadingProfitRules ? (
        <Spinner size="sm" />
       ) : roles.length === 0 ? (
        <Alert variant="info">
         {t("admin.profitRulesSettings.categoryRules.noRolesDefined")}
        </Alert>
       ) : (
        <>
         <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {roles.map((role) => (
           <Input
            key={role._id}
            label={`${role.name} (%)`}
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={categoryRolePercentages[role._id] || ""}
            onChange={(e) => handlePercentageChange(role._id, e.target.value)}
           />
          ))}
         </div>
         <div className="flex items-center gap-2">
          <Text className="font-medium text-sm">
           {t("admin.profitRulesSettings.categoryRules.total")}:{" "}
           {Object.values(categoryRolePercentages).reduce((sum, p) => sum + (p || 0), 0).toFixed(2)}%
          </Text>
          {Object.values(categoryRolePercentages).reduce((sum, p) => sum + (p || 0), 0) > 100 && (
           <Text className="text-red-600 text-sm">
            {t("admin.profitRulesSettings.categoryRules.errors.percentageExceeds100")}
           </Text>
          )}
         </div>
         <Text className="text-sm text-gray-500 italic">
          {t("admin.profitRulesSettings.categoryRules.saveWithCategory")}
         </Text>
        </>
       )}
      </Stack>
     </div>
    )}
   </form>
  </Modal>
 );
});

